import { FormState } from "@/app/features/shared/form-builder/types/types";
import { ZodIssue } from "zod";

export const fieldErrorResolver = (
    field: string,
    state: FormState
): {
    hasError: boolean;
    error: string;
} => {
    let fieldErrors = state.fieldErrors as unknown;

    if (Array.isArray(state.fieldErrors)) {
        fieldErrors = state.fieldErrors.map((fieldError: ZodIssue) => {
            if (fieldError.path.length > 1) {
                return {
                    ...fieldError,
                    path: [fieldError.path.join('.')],
                }
            }
            return fieldError;
        })
    }

    // Helper/Type Guards
    const isZodIssue = (x: unknown): x is ZodIssue =>
        !!x && typeof x === "object" && "message" in (x as never) && "code" in (x as never);

    const isZodIssueArray = (x: unknown): x is ZodIssue[] =>
        Array.isArray(x) && x.every(isZodIssue);

    const isRecord = (
        x: unknown
    ): x is Record<string, string | string[]> =>
        !!x && typeof x === "object" && !Array.isArray(x);

    const messages: string[] = [];

    if (!fieldErrors) {
        return { hasError: false, error: "" };
    }

    // Fall 1: Map
    if (isRecord(fieldErrors)) {
        const value = fieldErrors[field];
        if (typeof value === "string") {
            messages.push(value);
        } else if (Array.isArray(value)) {
            messages.push(...value);
        }
    }

    // Fall 2: Ein einzelnes ZodIssue
    else if (isZodIssue(fieldErrors)) {
        const issue = fieldErrors;
        const path0 = (issue.path?.[0] ?? undefined) as unknown;
        if (path0 === field) {
            messages.push(issue.message);
        }
    }

    // Fall 3: ZodIssue[]
    else if (isZodIssueArray(fieldErrors)) {
        for (const issue of fieldErrors) {
            const path0 = (issue.path?.[0] ?? undefined) as unknown;
            if (path0 === field) {
                messages.push(issue.message);
            }
        }
    }

    return {
        hasError: messages.length > 0,
        error: messages.join("\n"),
    };
};
